import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import { CheckCircle2, CircleDashed, Loader2, CreditCard } from "lucide-react";
import { ShieldCheck, Lock, BadgeCheck } from "lucide-react";
import { useAppointmentBookingStore } from "../../store/appointmentPayloadStore";
import { useNavigate } from "react-router-dom";
import { API } from "../../utils/axios";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
);

const StatusItem = ({
  label,
  state,
}: {
  label: string;
  state: "idle" | "loading" | "success";
}) => (
  <div
    className={`flex items-center justify-between py-2 border-b border-gray-50 last:border-0 transition-all ${
      state === "idle" ? "opacity-30" : "opacity-100"
    }`}
  >
    <div className="flex items-center gap-3">
      {state === "loading" ? (
        <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
      ) : state === "success" ? (
        <CheckCircle2 className="w-4 h-4 text-green-500" />
      ) : (
        <CircleDashed className="w-4 h-4 text-slate-300" />
      )}
      <span className="text-xs font-medium text-slate-600">{label}</span>
    </div>
    {state === "success" && <CheckCircle2 className="w-3 h-3 text-green-500" />}
  </div>
);

const CheckoutGrid = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [stages, setStages] = useState({
    intent: "idle",
    stripe: "idle",
    booking: "idle",
  });
  const { bookingPayload, clearBookingPayload } = useAppointmentBookingStore();

  const handleBooking = async () => {
    if (!bookingPayload?.amount || !bookingPayload?.baseAmount) {
      toast.error("Invalid Payment");
      return;
    }
    if (!stripe || !elements || !agreed) {
      toast.error("Payment service is still loading. Please try again shortly");
      return;
    }

    const cardElement = elements!.getElement(CardElement);

    if (!cardElement) {
      toast.error("Unable to access card details. Please refresh the page.");
      return;
    }

    if (!cardComplete) {
      toast.error("Please complete your card details.");
      return;
    }

    setLoading(true);

    try {
      setStages((s) => ({ ...s, intent: "loading" }));
      const { data } = await API.post(`/stripe/create-setup-intent`);
      setStages((s) => ({ ...s, intent: "success" }));

      const clientSecret = data.clientSecret;

      setStages((s) => ({ ...s, stripe: "loading" }));
      const result: any = await stripe!.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        const status = result.error.response?.status;
        if (status !== 401) {
          toast.error(result.error.message || "Error in saving card details");
          setLoading(false);
        }
        return;
      }
      setStages((s) => ({ ...s, stripe: "success" }));

      setStages((s) => ({ ...s, booking: "loading" }));

      const appointmentBooking = await API.post(`/appointment`, {
        ...bookingPayload,
        paymentMethodId: result.setupIntent.payment_method,
      });
      if (!appointmentBooking.data.success) {
        toast.error(appointmentBooking.data.message);
        setLoading(false);
        setStages({ intent: "idle", stripe: "idle", booking: "idle" });
        return;
      }
      setStages((s) => ({ ...s, booking: "success" }));
      setTimeout(() => {
        clearBookingPayload();
        setLoading(false);
        setStages({ intent: "idle", stripe: "idle", booking: "idle" });
        navigate("/appointment-success");
      }, 1500);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        toast.error("An error occurred. Please try again");
      }
      setLoading(false);
      setStages({ intent: "idle", stripe: "idle", booking: "idle" });
    }
  };

  useEffect(() => {
    if (!bookingPayload?.amount || !bookingPayload?.baseAmount) {
      navigate("/");
    }
  }, []);

  return (
    <div className="flex min-h-screen bg-[#F9F7F2] p-6 font-sans antialiased">
      <div className="relative w-full max-w-full flex flex-col md:flex-row gap-4">
        {/* LEFT SECTION: CONFIRM & PAY */}
        <div className="flex-1 bg-white max-w-5xl rounded-3xl p-8 lg:p-12 shadow-sm border border-gray-100 relative overflow-hidden">
          {/* LOADER OVERLAY */}
          {loading && (
            <div className="absolute inset-0 z-40 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center animate-in fade-in">
              <div className="w-72 bg-white p-8 rounded-3xl shadow-2xl">
                <div className="space-y-1">
                  <StatusItem
                    label="Securing Payment Channel"
                    state={stages.intent as any}
                  />
                  <StatusItem
                    label="Verifying with Bank"
                    state={stages.stripe as any}
                  />
                  <StatusItem
                    label="Saving Appointment"
                    state={stages.booking as any}
                  />
                </div>
                <div className="mt-6 flex justify-center">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white">
                    <CheckCircle2 size={14} />
                  </div>
                </div>
              </div>
            </div>
          )}

          <h1 className="text-4xl font-bold text-[#111827] mb-8">
            Confirm & Pay
          </h1>

          <div className="space-y-4 mb-8 text-lg font-medium border-b pb-5">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${bookingPayload?.baseAmount}</span>
            </div>
            <div className="flex justify-between">
              <span>Platform Fee (1.3%):</span>
              <span>${bookingPayload?.cardFee}</span>
            </div>
            <div className="flex justify-between">
              <span>Taxes ({bookingPayload?.taxPercentage || 0}%):</span>
              <span>${bookingPayload?.serviceTax}</span>
            </div>
          </div>

          {/* DELAYED CHARGE ALERT */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* LEFT SECTION */}
            <div className="space-y-6">
              {/* NO IMMEDIATE CHARGE */}
              <div className="p-4 bg-[#FDF4E7] rounded-xl border border-[#FBEAD2] flex gap-3 items-start">
                <CircleDashed className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700 leading-relaxed">
                  <span className="font-semibold">No Immediate Charge.</span>
                  <br />
                  Your card is saved, and you’ll be charged
                  <span className="font-semibold">
                    {" "}
                    1 hour after your service is completed
                  </span>
                  .
                </p>
              </div>

              {/* TOTAL AMOUNT */}
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Amount</p>
                <h3 className="text-3xl font-bold text-blue-600">
                  ${bookingPayload?.amount}
                </h3>
              </div>
            </div>

            {/* RIGHT SECTION */}
            <div className="space-y-6">
              {/* PAYMENT METHOD */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <CreditCard size={16} /> Payment Method
                </label>
                <div className="p-3 bg-white rounded-xl border border-blue-200 shadow-sm focus-within:ring-2 focus-within:ring-blue-100">
                  <CardElement
                    onChange={(e) => setCardComplete(e.complete)}
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#1e293b",
                          "::placeholder": { color: "#94A3B8" },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* TERMS */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="terms"
                  className="text-xs text-slate-500 font-medium cursor-pointer"
                >
                  I agree to the Terms of{" "}
                  <a
                    onClick={() => navigate("/refund-&-cancellation")}
                    className="text-blue-400 underline"
                  >
                    cancellation & refund policy
                  </a>
                </label>
              </div>

              {/* CTA */}
              <button
                onClick={handleBooking}
                disabled={!cardComplete || loading || !agreed}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-slate-300 text-white font-bold py-4 rounded-xl shadow-md transition-all text-lg active:scale-[0.98]"
              >
                Secure My Booking
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION: PEACE OF MIND */}
        <div className="w-full md:w-80 bg-white rounded-3xl shadow-sm border border-gray-100 h-fit">
          <div className="w-full max-w-md mx-auto p-6 sm:p-8 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="space-y-6 sm:space-y-8">
              {/* Item 1 */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-semibold text-gray-900">
                    Secure Payment Processing
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                    We never store your card details. All transactions are
                    processed securely through Stripe’s encrypted systems.
                  </p>
                </div>
              </div>

              {/* Item 2 */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-50 flex-shrink-0">
                  <Lock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-semibold text-gray-900">
                    Privacy Guaranteed
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                    Encrypted booking details. We never share your data.
                  </p>
                </div>
              </div>

              {/* Item 3 */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 flex-shrink-0">
                  <BadgeCheck className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-semibold text-gray-900">
                    PCI-DSS Certified
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                    Compliant with global payment card industry security
                    standards.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 sm:mt-8 pt-5 border-t border-gray-200 flex flex-col items-center text-center">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                  Secured by
                </span>
                <span
                  style={{ color: "#635bff" }}
                  className="text-xl sm:text-2xl font-bold"
                >
                  stripe
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LoadStripeCard = () => (
  <Elements stripe={stripePromise}>
    <CheckoutGrid />
  </Elements>
);
